import { Component, inject, signal } from '@angular/core';
import { ListService } from '@abp/ng.core';
import { Router } from '@angular/router';
import {
  CardComponent,
  CardBodyComponent,
  ConfirmationService,
} from '@abp/ng.theme.shared';
import {
  TreeDataInformationCreateDto,
  TreeDataInformationUpdateDto,
} from '../proxy/inventory/dtos/tree-data-information';
import { TREEDATAINFORMATION_CONFIG, TREEDATAINFORMATION_FIELDS, TREEDATAINFORMATION_PRE_ACCESS_FIELDS } from './tree-data-information.config';
import { BaseCrudComponent, CrudPage, GenericForm, ICrudService, PreAccessFilterModal, PreAccessFilterValue } from '@zendra/shared';
import { throwError } from 'rxjs';
import { TreeDataInformationPlanningOrchestratorService } from './tree-data-information-orchestrator.service';
import {
  GetTreeDataInformationProjectionListInputDto,
  TreeDataInformationProjectionDto,
  TreeDataInformationProjectionExcelDownloadDto,
} from './tree-data-information-projection.models';

@Component({
  selector: 'app-tree-data-information',
  imports: [
    CardComponent,
    CardBodyComponent,
    CrudPage,
    GenericForm,
    PreAccessFilterModal,
  ],
  providers: [ListService, ConfirmationService],
  templateUrl: './tree-data-information.html',
  styleUrl: './tree-data-information.scss',
})
export class TreeDataInformation extends BaseCrudComponent<
  TreeDataInformationProjectionDto,
  TreeDataInformationCreateDto,
  TreeDataInformationUpdateDto,
  TreeDataInformationProjectionExcelDownloadDto
> {
  private readonly orchestratorService = inject(TreeDataInformationPlanningOrchestratorService);
  private readonly router = inject(Router);

  readonly isPreAccessModalVisible = signal(true);
  readonly hasPreAccessSelection = signal(false);
  private preAccessParams: Partial<GetTreeDataInformationProjectionListInputDto> = {};

  service: ICrudService<
    TreeDataInformationProjectionDto,
    TreeDataInformationCreateDto,
    TreeDataInformationUpdateDto,
    TreeDataInformationProjectionExcelDownloadDto
  > = {
    create: (_input: TreeDataInformationCreateDto) =>
      throwError(() => new Error('Tree Data Information is read-only.')),
    update: (_id: number, _input: TreeDataInformationUpdateDto) =>
      throwError(() => new Error('Tree Data Information is read-only.')),
    deleteByIds: (_ids: number[]) =>
      throwError(() => new Error('Tree Data Information is read-only.')),
    getList: query => this.orchestratorService.getList(this.withPreAccessParams(query)),
    getDownloadToken: () => this.orchestratorService.getDownloadToken(),
    getListAsExcelFile: input => this.orchestratorService.getListAsExcelFile(this.withPreAccessParams(input)),
  };
  config = TREEDATAINFORMATION_CONFIG;
  fields = TREEDATAINFORMATION_FIELDS;
  preAccessOnlyFields = TREEDATAINFORMATION_PRE_ACCESS_FIELDS;

  onPreAccessFiltersApplied(filters: PreAccessFilterValue[]): void {
    const params = this.buildPreAccessParams(filters);
    if (Object.keys(params).length === 0) {
      this.toaster.error('Seleccione al menos un filtro para acceder a la tabla.');
      // The shared modal closes itself after emitting filtersApplied.
      setTimeout(() => this.isPreAccessModalVisible.set(true));
      return;
    }

    this.preAccessParams = params;
    this.hasPreAccessSelection.set(true);
  }

  onPreAccessFiltersClosed(): void {
    this.router.navigateByUrl('/');
  }

  private withPreAccessParams<T extends GetTreeDataInformationProjectionListInputDto>(input: T): T {
    const params = { ...input, ...this.preAccessParams } as T;

    for (const key of ['regionId', 'propertyId', 'standLocationId'] as const) {
      const ids = this.preAccessParams[key];
      if (!Array.isArray(ids)) continue;

      const gridIds = this.normalizeGridIds(input[key]);
      const effectiveIds = gridIds.length > 0 ? ids.filter(id => gridIds.includes(id)) : ids;
      // Empty IN filters are ignored by the backend; an impossible ID preserves an empty intersection.
      Object.assign(params, { [key]: (effectiveIds.length > 0 ? effectiveIds : [-2147483648]).join(',') });
    }

    return params;
  }

  private normalizeGridIds(value: unknown): number[] {
    let values: unknown[] = [];
    if (Array.isArray(value)) {
      values = value;
    } else if (typeof value === 'string') {
      values = value.split(',');
    } else if (typeof value === 'number') {
      values = [value];
    }
    return values.map(Number).filter(Number.isSafeInteger);
  }

  private buildPreAccessParams(
    filters: PreAccessFilterValue[],
  ): Partial<GetTreeDataInformationProjectionListInputDto> {
    const params: Partial<GetTreeDataInformationProjectionListInputDto> = {};

    for (const filter of filters) {
      if (filter.columnId === 'inventoryYear') {
        this.applyInventoryYearFilter(params, filter);
        continue;
      }

      const ids = (filter.searchTerms ?? [])
        .map(Number)
        .filter(value => Number.isSafeInteger(value) && value > 0);
      if (ids.length === 0) continue;

      switch (filter.columnId) {
        case 'regionId':
          params.regionId = ids;
          break;
        case 'propertyId':
          params.propertyId = ids;
          break;
        case 'standLocationIdFilter':
          params.standLocationId = ids;
          break;
      }
    }

    return params;
  }

  private applyInventoryYearFilter(
    params: Partial<GetTreeDataInformationProjectionListInputDto>,
    filter: PreAccessFilterValue,
  ): void {
    const [from, to] = filter.searchTerms ?? [];
    if (filter.mode === 'range') {
      if (this.isValidYear(from)) params.inventoryYearFrom = Number(from);
      if (this.isValidYear(to)) params.inventoryYearTo = Number(to);
    } else if (this.isValidYear(from)) {
      params.inventoryYear = Number(from);
    }
  }

  private isValidYear(value: unknown): boolean {
    return value !== null && value !== undefined && value !== ''
      && Number.isSafeInteger(Number(value)) && Number(value) > 0;
  }

  protected prepareUpdateDto(
    _data: any,
    _selected: TreeDataInformationProjectionDto,
  ): TreeDataInformationUpdateDto {
    throw new Error('Tree Data Information is read-only.');
  }

  protected getEntityName(): string {
    return 'TreeDataInformation';
  }
}
